import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import {
  FilePicker,
  type PickFilesResult
} from '@capawesome/capacitor-file-picker';
import { HttpClient } from '@angular/common/http';
import { ConfigService } from '../config.service';
import { EMPTY, from, mergeMap, Observable } from 'rxjs';
import { ErrorToastService } from '../error-toast.service';
import {
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_MB
} from '../../utils/constants/file.constants';

@Injectable({
  providedIn: 'root'
})
export class UploadDocumentsService {
  private readonly baseUrl: string;

  constructor(
    private http: HttpClient,
    private config: ConfigService,
    private errorToastService: ErrorToastService
  ) {
    this.baseUrl = this.config.baseRestURL;
  }

  /**
   * Открывает диалог выбора одного файла с ограничением по типу.
   * На нативе — Capacitor File Picker; в браузере — скрытый input[type=file].
   * Возвращает Promise<File[]> с одним файлом или пустой массив, если пользователь отменил выбор.
   */
  async pickFile(documentType: string): Promise<File[]> {
    const typesForPick =
      documentType === 'FORWARDER_FINAL_DOCS'
        ? [
            'application/zip',
            'application/x-7z-compressed',
            'application/vnd.rar'
          ]
        : [];
    const maxFileSize = MAX_FILE_SIZE_BYTES;

    try {
      if (Capacitor.isNativePlatform()) {
        const result: PickFilesResult = await FilePicker.pickFiles({
          limit: 1,
          types: typesForPick.length ? typesForPick : undefined
        });
        if (!result.files?.length) {
          return [];
        }
        const info = result.files[0];

        // Обработка нативных и base64-данных
        let blob: Blob;
        if (info.path) {
          // Native path → file://... → fetchable url
          const url = Capacitor.convertFileSrc(info.path);
          const res = await fetch(url);
          blob = await res.blob();
        } else if (info.data) {
          // iOS может вернуть base64, если readData = true
          blob = this.base64ToBlob(info.data, info.mimeType);
        } else {
          // нет данных
          return [];
        }

        const file = new File([blob], info.name, {
          type: info.mimeType,
          lastModified: info.modifiedAt ?? Date.now()
        });

        // Проверка размера файла
        if (maxFileSize && file.size > maxFileSize) {
          throw new Error(`Размер файла превышает ${MAX_FILE_SIZE_MB} MB`);
        }

        return [file];
      } else {
        return await new Promise<File[]>((resolve, reject) => {
          const input = document.createElement('input');
          input.type = 'file';
          if (typesForPick.length) {
            input.accept = typesForPick.join(',');
          }
          input.multiple = false;
          input.capture = '';
          input.style.display = 'none';

          input.onchange = () => {
            const files = input.files ? Array.from(input.files) : [];
            if (maxFileSize && files.length && files[0].size > maxFileSize) {
              reject(
                new Error(`Размер файла превышает ${MAX_FILE_SIZE_MB} MB`)
              );
              document.body.removeChild(input);
              return;
            }

            resolve(files);
            document.body.removeChild(input);
          };
          input.onerror = err => {
            reject(err);
            document.body.removeChild(input);
          };

          document.body.appendChild(input);
          input.click();
        });
      }
    } catch (err) {
      return Promise.reject(err);
    }
  }

  /**
   * Возвращает Observable<void>, который бросит ошибку
   * или успешно завершится без прогресса.
   */
  uploadDocumentsFlow(
    bargainId: string | number,
    documentType: string
  ): Observable<void> {
    return from(
      this.pickFile(documentType).catch(error => {
        // Обработка ошибок при выборе файла (например, превышение размера)
        this.errorToastService.showError(
          error.message || 'Ошибка при выборе файла'
        );
        return [];
      })
    ).pipe(
      mergeMap(files => {
        if (!files.length) {
          // отмена выбора или ошибка
          return EMPTY;
        }

        const fd = new FormData();
        fd.append('documentType', documentType);
        fd.append('files', files[0], files[0].name);

        const url = `${this.baseUrl}order/bargain/${bargainId}/upload/document`;

        return this.http.post<void>(url, fd);
      })
    );
  }

  /**
   * Преобразует строку Base64 в Blob.
   */
  private base64ToBlob(base64: string, mimeType: string): Blob {
    const byteString = atob(base64);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mimeType });
  }
}
